'use client';

import React, { useMemo } from 'react';

import { FundingFactory, FundingKey, FundingModel, FundingType } from '@bytel/leviator';
import { DirectionEnum, FlexBox, RadioTile } from '@bytel/trilogy-react';

import { MobilePlanType } from '@app-types/plan';

import { formatPrice } from '@helpers/wording';

import { PricesDetails, sendProductPhoneFundingTracking } from '@product-detail/helpers';

import { FundingKeySelector, FundingTypeSelector, OdrInfos } from './funding';

export type SelectFundingProps = {
    currentPlan: MobilePlanType | null;
    fundingKey?: FundingKey;
    onFundingKeyChange: (key: FundingKey) => void;
    isRenewal: boolean;
    prices: PricesDetails;
    withoutPlanPrice: number;
};

export function SelectFunding({
    currentPlan,
    fundingKey,
    onFundingKeyChange,
    isRenewal,
    prices,
    withoutPlanPrice,
}: Readonly<SelectFundingProps>) {
    const handleFundingKeyChange = (key: FundingKey) => {
        sendProductPhoneFundingTracking(isRenewal, 'Oui');
        onFundingKeyChange(key);
    };

    const hasOdr = useMemo(() => Math.abs(prices.productPrices.odrRefund) > 0, [prices.productPrices.odrRefund]);

    const { fundingTypes, fundingKeysByType } = useMemo(() => {
        const models = (currentPlan?.fundings ?? []).map((f) => new FundingModel(f));

        const types = models.reduce<FundingModel[]>((acc, val) => {
            if (acc.some((f) => f.type === val.type)) return acc;
            acc.push(val);
            return acc;
        }, []);

        const byType = models.reduce<{ [key in FundingType]?: FundingModel[] }>((acc, val) => {
            acc[val.type] ??= [];
            acc[val.type]!.push(val);
            return acc;
        }, {});

        return { fundingTypes: types, fundingKeysByType: byType };
    }, [currentPlan?.fundings]);

    const selectedBaseType = fundingKey ? FundingFactory.splitKey(fundingKey).type : null;
    const selectedTypeOptions = selectedBaseType ? (fundingKeysByType[selectedBaseType] ?? []) : [];
    const hasMultipleInstallments = selectedTypeOptions.length > 1;

    const installmentModels = useMemo(() => {
        if (!selectedBaseType) return [];
        return selectedTypeOptions.toSorted((a, b) => (a.installments ?? 0) - (b.installments ?? 0));
    }, [selectedBaseType, selectedTypeOptions]);

    const handleTypeChange = (type: FundingType) => {
        const options = fundingKeysByType[type] ?? [];
        const sorted = [...options].sort((a, b) => a.initialDeposit - b.initialDeposit);
        if (sorted[0]) {
            handleFundingKeyChange(sorted[0].key);
        }
    };

    if (currentPlan === null) {
        return (
            <RadioTile
                description={
                    <>
                        En une seule fois
                        {hasOdr ? (
                            <OdrInfos
                                odrRefund={Math.abs(prices.productPrices.odrRefund)}
                                phonePriceWithOdrDeducted={withoutPlanPrice}
                            />
                        ) : null}
                    </>
                }
                checked
                id='no-funding'
                label={formatPrice(withoutPlanPrice)}
                horizontal
            />
        );
    }

    if (fundingTypes.length === 0) return null;
    return (
        <FlexBox gap={4} direction={DirectionEnum.COLUMN}>
            <FundingTypeSelector
                value={selectedBaseType}
                options={fundingTypes}
                onChange={handleTypeChange}
                fundingKeysByType={fundingKeysByType}
                hasOdr={hasOdr}
                odrRefund={Math.abs(prices.productPrices.odrRefund)}
                phonePriceWithOdrDeducted={prices.productPrices.marketingAmount}
                hasMultipleInstallments={hasMultipleInstallments}
            />
            {(selectedBaseType === FundingType.YOUNITED || selectedBaseType === FundingType.SWAPPY) && (
                <FundingKeySelector
                    value={fundingKey ?? ''}
                    options={installmentModels}
                    onChange={handleFundingKeyChange}
                    hasOdr={hasOdr}
                    odrRefund={Math.abs(prices.productPrices.odrRefund)}
                    phonePriceWithOdrDeducted={prices.productPrices.marketingAmount}
                />
            )}
        </FlexBox>
    );
}
